import React, { useState } from 'react';

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  last4: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
  bankName?: string;
  accountType?: 'checking' | 'savings';
}

export interface PaymentMethodsProps {
  paymentMethods: PaymentMethod[];
  onAdd: () => void;
  onEdit: (methodId: string) => void;
  onDelete: (methodId: string) => void;
  onSetDefault: (methodId: string) => void;
  className?: string;
  loading?: boolean;
}

export const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  paymentMethods,
  onAdd,
  onEdit,
  onDelete,
  onSetDefault,
  className = '',
  loading = false,
}) => {
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const getCardIcon = (brand?: string) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
      case 'american_express':
        return '💳';
      case 'discover':
        return '💳';
      default:
        return '💳';
    }
  };

  const getBankIcon = () => {
    return '🏦';
  };

  const formatCardBrand = (brand?: string) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  };

  const formatExpiry = (month?: number, year?: number) => {
    if (!month || !year) return '';
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  const handleAction = async (action: () => void, methodId?: string) => {
    if (methodId) {
      setActionLoading(methodId);
    }
    try {
      await action();
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className={className} style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem',
            color: '#6b7280',
          }}
        >
          <div
            style={{
              width: '2rem',
              height: '2rem',
              border: '2px solid #e5e7eb',
              borderTop: '2px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '1rem',
            }}
          ></div>
          <p>Loading payment methods...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
        }}
      >
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1f2937',
            margin: 0,
          }}
        >
          Payment Methods
        </h2>
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
          onClick={() => handleAction(onAdd)}
        >
          <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>+</span>
          Add Payment Method
        </button>
      </div>

      {paymentMethods.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem',
            border: '2px dashed #e5e7eb',
            borderRadius: '12px',
            color: '#6b7280',
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💳</div>
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#374151',
              margin: '0 0 0.5rem 0',
            }}
          >
            No payment methods
          </h3>
          <p style={{ margin: '0 0 2rem 0' }}>
            Add a payment method to start making payments.
          </p>
          <button
            style={{
              padding: '1rem 2rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onClick={() => handleAction(onAdd)}
          >
            Add Your First Payment Method
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {paymentMethods.map(method => (
            <div
              key={method.id}
              style={{
                border: method.isDefault
                  ? '2px solid #10b981'
                  : '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '1.5rem',
                background: method.isDefault ? '#f0fdf4' : 'white',
                position: 'relative',
                transition: 'all 0.3s ease',
              }}
            >
              {method.isDefault && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '1rem',
                    background: '#10b981',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  Default
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                }}
              >
                <div style={{ fontSize: '2rem', flexShrink: 0 }}>
                  {method.type === 'card'
                    ? getCardIcon(method.brand)
                    : getBankIcon()}
                </div>
                <div style={{ flex: 1 }}>
                  {method.type === 'card' ? (
                    <>
                      <div
                        style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#1f2937',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {formatCardBrand(method.brand)} •••• {method.last4}
                      </div>
                      {method.expiryMonth && method.expiryYear && (
                        <div
                          style={{
                            fontSize: '0.875rem',
                            color: '#6b7280',
                          }}
                        >
                          Expires{' '}
                          {formatExpiry(method.expiryMonth, method.expiryYear)}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#1f2937',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {method.bankName || 'Bank Account'} •••• {method.last4}
                      </div>
                      <div
                        style={{
                          fontSize: '0.875rem',
                          color: '#6b7280',
                        }}
                      >
                        {method.accountType
                          ? method.accountType.charAt(0).toUpperCase() +
                            method.accountType.slice(1)
                          : 'Bank Account'}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                }}
              >
                {!method.isDefault && (
                  <button
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flex: 1,
                      minWidth: '80px',
                      opacity: actionLoading === method.id ? 0.6 : 1,
                    }}
                    onClick={() =>
                      handleAction(() => onSetDefault(method.id), method.id)
                    }
                    disabled={actionLoading === method.id}
                  >
                    {actionLoading === method.id
                      ? 'Loading...'
                      : 'Set as Default'}
                  </button>
                )}
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: '#f3f4f6',
                    color: '#374151',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flex: 1,
                    minWidth: '80px',
                    opacity: actionLoading === method.id ? 0.6 : 1,
                  }}
                  onClick={() =>
                    handleAction(() => onEdit(method.id), method.id)
                  }
                  disabled={actionLoading === method.id}
                >
                  {actionLoading === method.id ? 'Loading...' : 'Edit'}
                </button>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#ef4444',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flex: 1,
                    minWidth: '80px',
                    opacity: actionLoading === method.id ? 0.6 : 1,
                  }}
                  onClick={() =>
                    handleAction(() => onDelete(method.id), method.id)
                  }
                  disabled={actionLoading === method.id}
                >
                  {actionLoading === method.id ? 'Loading...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
